import {
	CardType,
	type CardConfig,
	type AnalysisCardConfig,
} from '../models/card-model';
import type { ChartCardConfig } from '../models/chart-config.model';
import type { TableCardConfig } from '../models/table-config.model';

/**
 * Unified change type for all card configuration mutations.
 * All changes (chart type, AI patch, data tab, custom JSON) use this single type.
 */
export interface ConfigChange {
	type: 'patch';
	origin: ConfigChangeOrigin;
	patch: Partial<CardConfig>;
	/**
	 * `false` when the patch is already saved server-side (e.g. an agent tool call
	 * that persisted it): apply locally but do not queue another `updateCard`.
	 * Only the default merge pipeline honors it; chart and table pipelines
	 * still persist their repaired patch.
	 */
	persist?: boolean;
}

export type ConfigChangeOrigin = 'form' | 'ai' | 'host' | 'undo' | 'runtime';

/**
 * Severity levels for validation warnings.
 */
export enum WarningSeverity {
	/** Silent badge indicator only */
	Silent = 'silent',
	/** Toast notification */
	Info = 'info',
	/** Warning banner in UI */
	Warning = 'warning',
	/** Blocking modal - user must resolve */
	Blocking = 'blocking',
}

/**
 * A validation warning for user feedback.
 */
export interface ValidationWarning {
	/** Machine-readable warning code */
	code: string;
	/** Human-readable message */
	message: string;
	/** Severity level */
	severity: WarningSeverity;
	/** Optional: field path related to warning (e.g., 'data.metrics') */
	field?: string;
	/** Optional: action user can take */
	suggestedAction?: string;
}

/**
 * Result of applying a configuration change.
 */
export interface ChangeResult<T extends CardConfig = CardConfig> {
	/** The new configuration after applying the change */
	config: T;
	/** Validation warnings (empty if no issues) */
	warnings: ValidationWarning[];
	/** Whether any data migration occurred */
	migrated: boolean;
}

/**
 * Type guard to check if a card is a chart card.
 */
export function isChartCardConfig(
	card: CardConfig
): card is CardConfig & { chartType: string; visualization?: unknown } {
	return (
		card.cardType === 'chart' &&
		'chartType' in card &&
		typeof (card as { chartType?: string }).chartType === 'string'
	);
}

/** Type guard for table card configuration. */
export function isTableCardConfig(card: CardConfig): card is TableCardConfig {
	return (
		card.cardType === CardType.Table ||
		(card as { cardType?: string }).cardType === 'table'
	);
}

/** Type guard for analysis card configuration. */
export function isAnalysisCardConfig(
	card: CardConfig
): card is AnalysisCardConfig {
	return card.cardType === CardType.Analysis;
}
